import { useEffect, useRef, useState } from 'react';
import { ConfirmDialog } from './Modal';
import { SavedFilterPicker } from './SavedFilterPicker';
import { useToast } from './Toast';
import { useDeleteSavedFilter, useSavedFilters } from '../hooks/useSavedFilters';
import type { SelectedEntity } from './EntityFilterSelect';
import type { SavedFilterDto } from '../lib/types';

interface SavedFiltersMenuProps {
  /** The currently-applied saved filter for this view, or null if none. */
  applied: SavedFilterDto | null;
  /** Called with the filter to apply, or null to clear. */
  onApply: (filter: SavedFilterDto | null) => void;
  /** The filter bar's current worker selection — what "Save current selection as…" saves. */
  currentPeople?: SelectedEntity[];
  /** The filter bar's current project selection. */
  currentProjects?: SelectedEntity[];
}

/**
 * Dropdown control for the schedule filter bars: apply/clear a saved filter, create one from
 * whatever the filter bar currently has selected, and edit / delete existing ones. Applying a
 * filter loads its members into the bar (see useScheduleFilterState), so the bar stays the one
 * place a selection is read and edited.
 */
export function SavedFiltersMenu({
  applied,
  onApply,
  currentPeople,
  currentProjects,
}: SavedFiltersMenuProps) {
  const { data: filters, isLoading } = useSavedFilters();
  const deleteMutation = useDeleteSavedFilter();
  const { success, error: toastError } = useToast();

  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editingFilter, setEditingFilter] = useState<SavedFilterDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SavedFilterDto | null>(null);

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (!menuRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function openCreate() {
    setEditingFilter(null);
    setPickerOpen(true);
    setOpen(false);
  }

  function openEdit(f: SavedFilterDto) {
    setEditingFilter(f);
    setPickerOpen(true);
    setOpen(false);
  }

  async function handleDelete(f: SavedFilterDto) {
    try {
      await deleteMutation.mutateAsync(f.id);
      success('Saved filter deleted.');
      if (applied?.id === f.id) onApply(null);
    } catch {
      toastError('Could not delete the saved filter.');
    }
  }

  function handleSaved(saved: SavedFilterDto) {
    // Editing the filter currently applied, or creating a brand new one, both become "active".
    if (!editingFilter || applied?.id === editingFilter.id) onApply(saved);
  }

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={`border rounded px-3 py-1 text-sm flex items-center gap-1 max-w-[220px] ${
          applied
            ? 'bg-brand-blue text-white border-brand-blue'
            : 'border-slate-300 text-brand-charcoal hover:bg-slate-100'
        }`}
      >
        <span className="truncate">{applied ? applied.name : 'Saved Filters'}</span>
        <span aria-hidden="true">▾</span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute top-full left-0 mt-1 bg-white rounded-md shadow-lg border border-slate-200 py-1 min-w-[280px] z-30 text-sm"
        >
          {isLoading && <div className="px-3 py-2 text-slate-500">Loading…</div>}
          {!isLoading && (filters ?? []).length === 0 && (
            <div className="px-3 py-2 text-slate-500">No saved filters yet.</div>
          )}
          {(filters ?? []).map((f) => (
            <div
              key={f.id}
              className={`flex items-center gap-2 px-3 py-1.5 ${applied?.id === f.id ? 'bg-brand-blue/10' : 'hover:bg-slate-50'}`}
            >
              <button
                type="button"
                className="flex-1 text-left truncate"
                onClick={() => { onApply(f); setOpen(false); }}
                title={f.name}
              >
                <span className={applied?.id === f.id ? 'text-brand-blue font-medium' : 'text-brand-charcoal'}>
                  {f.name}
                </span>
                <span className="block text-xs text-slate-500">
                  {f.personIds.length} {f.personIds.length === 1 ? 'person' : 'people'} · {f.projectIds.length} projects
                </span>
              </button>
              <button type="button" onClick={() => openEdit(f)} className="text-xs text-brand-blue hover:underline">
                Edit
              </button>
              <button type="button" onClick={() => setDeleteTarget(f)} className="text-xs text-red-600 hover:underline">
                Delete
              </button>
            </div>
          ))}
          <div className="border-t border-slate-200 mt-1 pt-1">
            {applied && (
              <button
                type="button"
                onClick={() => { onApply(null); setOpen(false); }}
                className="w-full text-left px-3 py-1.5 text-xs text-brand-blue hover:bg-slate-50"
              >
                Clear applied filter
              </button>
            )}
            <button
              type="button"
              onClick={openCreate}
              className="w-full text-left px-3 py-1.5 text-xs text-brand-blue hover:bg-slate-50"
            >
              Save current selection as…
            </button>
          </div>
        </div>
      )}

      <SavedFilterPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        editingFilter={editingFilter}
        initialPersonIds={!editingFilter ? currentPeople?.map((p) => p.id) : undefined}
        initialPersonNames={!editingFilter ? currentPeople?.map((p) => p.label) : undefined}
        initialProjectIds={!editingFilter ? currentProjects?.map((p) => p.id) : undefined}
        initialProjectNames={!editingFilter ? currentProjects?.map((p) => p.label) : undefined}
        onSaved={handleSaved}
      />

      {deleteTarget && (
        <ConfirmDialog
          open={true}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => { void handleDelete(deleteTarget); setDeleteTarget(null); }}
          title="Delete saved filter?"
          message={`Delete "${deleteTarget.name}"? This can't be undone.`}
          confirmLabel="Delete"
          destructive
        />
      )}
    </div>
  );
}
